import Reveal from "@/components/base/Reveal";

export default function Specs() {
  const rows = [
    { k: "Model", v: "NOVA v1.2" },
    { k: "Material", v: "Anodized aluminum" },
    { k: "Finish", v: "Matte graphite" },
    { k: "Weight", v: "184 g" },
    { k: "Height", v: "72 mm" },
    { k: "Diameter", v: "58 mm" },
    { k: "Response", v: "< 120 ms" },
    { k: "Battery", v: "36 hours" },
    { k: "Languages", v: "42" },
    { k: "Connectivity", v: "Wi-Fi 6, BLE 5.3" },
  ];

  const highlights = [
    { k: "184", u: "g", label: "Handheld weight" },
    { k: "72", u: "mm", label: "Height, no more" },
    { k: "36", u: "h", label: "Between charges" },
  ];

  return (
    <section
      id="specs"
      className="w-full px-6 md:px-10 py-24 md:py-32 border-t border-background-200"
    >
      {/* Section header row */}
      <Reveal y={30}>
        <div className="flex items-end justify-between mb-16 md:mb-24">
          <p className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-500">
            <span>03 — Specifications</span>
          </p>
          <p className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-500 hidden md:block">
            <span>Only what shapes the object</span>
          </p>
        </div>
      </Reveal>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.15fr] gap-12 lg:gap-16 items-start">
        {/* LEFT — hero portrait + heading + intro */}
        <div className="flex flex-col">
          <Reveal y={60} duration={1000}>
            <div className="relative w-full aspect-[4/5] rounded-lg overflow-hidden bg-background-100 border border-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Extreme%20macro%20technical%20detail%20of%20a%20matte%20black%20aluminum%20AI%20device%20on%20a%20warm%20gray%20paper%20surface%20with%20thin%20engineering%20measurement%20lines%20drawn%20subtly%20around%20it%2C%20dramatic%20side%20light%2C%20monochrome%20muted%20background%2C%20editorial%20industrial%20design%20blueprint%20study%2C%20refined%20negative%20space%2C%20high%20end%20studio%20photography&width=1200&height=1500&seq=topbud-specs-main&orientation=portrait"
                alt="Topbud dimensional study"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute left-4 top-4 flex items-center gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />
                <span className="font-label text-[10px] tracking-[0.22em] uppercase text-background-50">
                  Fig. 03 / Dimension
                </span>
              </div>
              <span className="absolute right-4 bottom-4 font-label text-[10px] tracking-[0.22em] uppercase text-background-50/80">
                72 × 58 mm
              </span>
            </div>
          </Reveal>

          <div className="mt-10 max-w-md">
            <Reveal delay={120} y={40}>
              <h2 className="font-heading font-semibold text-[48px] md:text-[68px] leading-[0.95] tracking-[-0.03em] text-foreground-950">
                The <br />
                <span className="italic font-normal text-foreground-700">
                  essentials.
                </span>
              </h2>
            </Reveal>
            <Reveal delay={240} y={40}>
              <p className="mt-8 text-[13px] leading-[1.75] text-foreground-700 text-justify">
                No spec sheet olympics. Only the numbers that shape how it feels
                in your hand and in the room — the weight, the silence between
                words, the hours before the next charge.
              </p>
            </Reveal>
          </div>
        </div>

        {/* RIGHT — highlights + full table */}
        <div className="flex flex-col">
          {/* Highlight numbers */}
          <div className="grid grid-cols-3 gap-4 md:gap-6 mb-14 md:mb-16">
            {highlights.map((h, i) => (
              <Reveal key={h.label} delay={i * 140} y={40}>
                <div className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-6 h-full flex flex-col justify-between">
                  <div className="flex items-baseline gap-1">
                    <span className="font-heading font-semibold text-[44px] md:text-[56px] leading-none tracking-[-0.03em] text-foreground-950">
                      {h.k}
                    </span>
                    <span className="font-heading text-[16px] md:text-[18px] leading-none tracking-[-0.02em] text-foreground-600">
                      {h.u}
                    </span>
                  </div>
                  <p className="mt-6 font-label text-[10px] tracking-[0.22em] uppercase text-foreground-500">
                    {h.label}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Full table */}
          <Reveal delay={200} y={30}>
            <div className="flex items-center justify-between mb-4">
              <p className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-500">
                Full sheet
              </p>
              <p className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-500">
                Rev. 04 · 2025
              </p>
            </div>
          </Reveal>
          <div className="border-t border-foreground-300">
            {rows.map((r, i) => (
              <Reveal key={r.k} delay={i * 60} y={20} duration={700}>
                <div className="flex items-center justify-between py-4 border-b border-background-200 group">
                  <div className="flex items-center gap-4">
                    <span className="font-label text-[10px] tracking-[0.22em] uppercase text-foreground-400 tabular-nums">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-600">
                      {r.k}
                    </span>
                  </div>
                  <span className="font-body text-[14px] text-foreground-950 tabular-nums">
                    {r.v}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={300} y={30}>
            <div className="mt-8 flex items-center gap-4">
              <span className="h-px w-10 bg-foreground-400" />
              <span className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-600">
                Full technical sheet available upon reservation
              </span>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}